import { omit } from 'lodash-es';
import {
  products,
  visionProducts,
  integrations,
} from '../../libs/static/productsAndIntegrations.enum';

export type ProductDetails = Record<string, unknown>;

export interface ProductSelection {
  productSelection?: Record<string, boolean>;
  integrations?: Record<string, boolean>;
}

// Every product/integration that can own a productDetails entry, across both builds: a Vision
// order can carry keys written by the Matrix build and vice versa.
const KNOWN_PRODUCT_DETAIL_KEYS: readonly string[] = [
  ...products,
  ...visionProducts,
  ...integrations,
].map(option => option.value);

const selectedKeys = (selection: ProductSelection | undefined): string[] => [
  ...Object.keys(selection?.productSelection ?? {}),
  ...Object.keys(selection?.integrations ?? {}),
];

/**
 * Builds the `productDetails` to store when the Products step is submitted.
 *
 * The Products step owns only the per-product subform entries, but it used to replace
 * `productDetails` wholesale — deleting every key the Product Order Details step had written
 * (smallBusinessUnit, the contacts, the order notes) and PUTting that loss straight to the API
 * (DO-295). So merge instead, and drop only the entries of products that are no longer
 * selected, which is the one useful thing the wholesale replace was doing.
 *
 * `previousSelection` covers detail keys for products that have since been removed from the
 * enums (e.g. MX4, WTN) and so aren't in KNOWN_PRODUCT_DETAIL_KEYS; without it they would
 * linger forever and leak into the confirmation email as raw rows.
 *
 * Pruning is keyed off `nextSelection` rather than the supplied subforms: a selected product
 * with no subform schema produces no subform entry, and dropping it would delete the object
 * finalizeAndSubmitOrder later assigns `.pricing` to.
 */
export const mergeProductDetails = ({
  existingDetails,
  previousSelection,
  nextSelection,
  subForms,
}: {
  existingDetails: ProductDetails | undefined;
  previousSelection: ProductSelection | undefined;
  nextSelection: ProductSelection;
  subForms: ProductDetails;
}): ProductDetails => {
  const stillSelected = new Set(selectedKeys(nextSelection));
  const stale = Array.from(
    new Set([...KNOWN_PRODUCT_DETAIL_KEYS, ...selectedKeys(previousSelection)]),
  ).filter(key => !stillSelected.has(key));

  return { ...omit(existingDetails ?? {}, stale), ...subForms };
};
